import { AsyncPipe, NgIf } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, distinctUntilChanged, filter, map, merge, of } from 'rxjs';
import { AuthService } from '../../../auth/services/auth.service';
import { EXTERNAL_URLS } from '../../../../shared/config/external-urls';
import { ToolbarTitleService } from './toolbar-title.service';

interface RouteTitleEntry {
    pattern: RegExp;
    resolveTitle: (
        urlMatch: RegExpExecArray,
        contextualTitle: string | null,
    ) => string;
}

const ROUTE_TITLE_DICTIONARY: readonly RouteTitleEntry[] = [
    {
        pattern: /^\/translation-manager\/([^/?#]+)/,
        resolveTitle: (urlMatch, contextualTitle) => {
            if (contextualTitle) {
                return contextualTitle;
            }

            const detailIdentifier = decodeURIComponent(urlMatch[1] ?? '').trim();
            return detailIdentifier ? `Request ${detailIdentifier}` : 'Request';
        },
    },
    {
        pattern: /^\/translation-manager(?:$|[?#])?/,
        resolveTitle: () => 'Translation Manager',
    },
];

@Component({
    selector: 'app-toolbar',
        imports: [NgIf, AsyncPipe, MatToolbarModule, MatButtonModule],
    templateUrl: './toolbar.component.html',
    styleUrl: './toolbar.component.scss'
})
export class ToolbarComponent {
    private authService = inject(AuthService);
    private router = inject(Router);
    private toolbarTitleService = inject(ToolbarTitleService);

    readonly toolbarTitle$ = combineLatest([
        merge(
            of(this.router.url),
            this.router.events.pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                map((event) => event.urlAfterRedirects),
            ),
        ),
        this.toolbarTitleService.contextualTitle$,
    ]).pipe(
        map(([url, contextualTitle]) => this.resolveToolbarTitle(url, contextualTitle)),
        distinctUntilChanged(),
    );

    readonly prid$ = merge(
        of(this.authService.getAuthFromLocalStorage()),
        this.authService.currentUser$
    ).pipe(
        map((auth) => auth?.payload?.prid ?? null),
        distinctUntilChanged()
    );

    readonly profileImageBaseUrl = EXTERNAL_URLS.profileImageBaseUrl;

    private resolveToolbarTitle(url: string, contextualTitle: string | null): string {
        for (const entry of ROUTE_TITLE_DICTIONARY) {
            const urlMatch = entry.pattern.exec(url);
            if (urlMatch) {
                return entry.resolveTitle(urlMatch, contextualTitle);
            }
        }

        return 'Vendor Translations';
    }

    getProfileImageUrl(prid: string): string {
        return `${this.profileImageBaseUrl}/${prid}?appid=translate-vendor`;
    }
}
